import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { SalesPeriodFilter } from '../../../api/projectManagement';
import { useSalesOverview } from '../../../hooks/useProjectManagement';
import { ErrorState, LoadingState } from '../../../components/ui/States';
import { formatDayMonthTime } from '../../../utils/dateFormat';
import { PipelineOverview } from './PipelineOverview';
import { RepPipelineTable } from './RepPipelineTable';
import { StaleDealsSection } from './StaleDeals';
import { currentQuarter } from '../../ProjectManagement/Sales/dealKinds';
import { SectionToolbar } from '../SectionToolbar';

/**
 * Sales → Pipeline: the AE overview, the per-rep split and the stale deals to
 * clean up, stacked on one page. `/sales/pipeline#stale` lands on the stale list.
 */
export function PipelinePage() {
  // Opens on the current quarter; "All time" stays one click away.
  const [filter, setFilter] = useState<SalesPeriodFilter | null>(() => ({ quarters: [currentQuarter()], from: null, to: null }));
  const query = useSalesOverview(filter);
  const { hash } = useLocation();
  const staleRef = useRef<HTMLDivElement | null>(null);
  const data = query.data;

  const openStale = () => staleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  useEffect(() => {
    if (hash === '#stale' && data) openStale();
  }, [hash, data]);

  if (query.error) {
    return <ErrorState title="Couldn’t load the pipeline" error={query.error} onRetry={() => query.refetch()} />;
  }
  if (!data) return <LoadingState rows={5} />;

  return (
    <div className="space-y-5">
      <SectionToolbar
        meta={`From Attio · ${formatDayMonthTime(data.generatedAt)}`}
        info={
          <>
            Open pipeline is as of today. <strong>Active</strong> deals moved stage, or had an email or meeting with
            the account in Attio, in the last {data.staleAfterDays} days; <strong>stale</strong> deals had neither. Only the closed line follows the period dropdown; the pipeline by
            quarter is never filtered so quarters stay comparable.
          </>
        }
      />
      <PipelineOverview data={data} filter={filter} onFilterChange={setFilter} onOpenStale={openStale} />
      <RepPipelineTable />
      <div ref={staleRef} className="scroll-mt-4">
        <StaleDealsSection
          deals={data.staleDeals}
          staleAfterDays={data.staleAfterDays}
          totalValueEur={data.totals.staleValueEur}
        />
      </div>
    </div>
  );
}
